import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, map, shareReplay, startWith, tap, withLatestFrom } from 'rxjs/operators';

interface PaginationPagePointer {
  index: number;
  text: string;
}

@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginatorComponent implements OnInit, OnDestroy {

  private subscriptions = new Map<string, Subscription>();

  @Input() input$: Observable<Array<unknown>>;
  @Input() itemsPerPage = 10;
  @Input() filter$: Observable<string>;
  @Input() $output: Subject<Array<unknown>>;

  public filteredItems$: Observable<Array<unknown>>;

  public $selectedPage = new Subject<number>();
  public $pageAdjuster = new Subject<number>();

  public selectedPage$ = this.$selectedPage.pipe(distinctUntilChanged(), shareReplay(1), startWith(0));

  public pagePointers$: Observable<PaginationPagePointer[]>;
  public isFirstPageSelected$: Observable<boolean>;
  public isLastPageSelected$: Observable<boolean>;

  public selectedItems$: Observable<Array<unknown>>;

  constructor() { }

  ngOnInit(): void {

    // items that match the filter
    this.filteredItems$ = combineLatest([
      this.input$,
      this.filter$.pipe(
        debounceTime(250),
        map(x => x?.toLocaleLowerCase() || ''),
        distinctUntilChanged(),
        startWith(''),
      ),
    ])
      .pipe(
        map(([items, filter]) =>
          items.filter(item => JSON.stringify(Object.values(item)).toLocaleLowerCase().includes(filter))
        ),
        shareReplay(1)
      );

    // setup page pointers
    this.pagePointers$ = this.filteredItems$.pipe(
      map(items => {
        const pages = Math.max(1, Math.ceil(items.length / this.itemsPerPage));
        // generate a numbered array of page pointer objects
        return new Array(pages).fill(true).map((_, i) => ({ index: i, text: `${i + 1}` } as PaginationPagePointer));
      }),
      shareReplay(1)
    );

    // +1/-1 stepper
    this.subscriptions.set('adjustPages',
      this.$pageAdjuster.pipe(withLatestFrom(this.selectedPage$))
        .subscribe(([adjuster, selected]) => this.$selectedPage.next(selected + adjuster))
    );

    // first page selected indicator
    this.isFirstPageSelected$ = combineLatest([
      this.pagePointers$,
      this.selectedPage$
    ])
      .pipe(
        map(([pages, selected]) => !pages.length || selected === 0),
        shareReplay(1)
      );

    // last page selected indicator
    this.isLastPageSelected$ = combineLatest([
      this.pagePointers$,
      this.selectedPage$
    ])
      .pipe(
        map(([pages, selected]) => !pages.length || selected === pages.length - 1),
        shareReplay(1)
      );

    // handle new page selection
    this.selectedItems$ = combineLatest([
      this.filteredItems$,
      this.selectedPage$
    ])
      .pipe(
        map(([items, selectedPage]) => {
          const itemsPerPage = this.itemsPerPage;
          const index = selectedPage * itemsPerPage;
          return items.slice(index, index + itemsPerPage);
        })
      );

    // emit when page changes
    this.subscriptions.set('selectedItems$',
      this.selectedItems$.pipe(delay(10)).subscribe(items => this.$output.next(items))
    );

    // clamp selected page if the array shrinks, for example due to filters
    this.subscriptions.set('reset selectedPage$',
      combineLatest([
        this.pagePointers$,
        this.selectedPage$
      ]).subscribe(([pages, selected]) => {
        if (selected > pages.length - 1) {
          this.$selectedPage.next(0);
        }
      })
    );

  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

}
